import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export const AUTH_STORAGE_KEY = 'glassorder-auth';

// Cart Store
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      tableId: null,
      establishmentId: null,

      setTable: (tableId, establishmentId) => set({ tableId, establishmentId }),

      addItem: (product, selectedOptions = []) => {
        const items = get().items;
        const optionKey = [...selectedOptions].sort().join('|');

        const existing = items.find(
          (i) => i.product_id === product.id && (i.optionKey || '') === optionKey
        );

        if (existing) {
          set({
            items: items.map((i) =>
              i.product_id === product.id && (i.optionKey || '') === optionKey
                ? { ...i, quantity: i.quantity + 1 }
                : i
            )
          });
        } else {
          const optionsCost = selectedOptions.reduce((sum, optName) => {
            const opt = (product.options || []).find((o) => o.name === optName);
            return sum + (opt?.price || 0);
          }, 0);

          set({
            items: [
              ...items,
              {
                product_id: product.id,
                name: product.name,
                price: product.price + optionsCost,
                base_price: product.price,
                category_id: product.category_id,
                image: product.image,
                quantity: 1,
                selected_options: selectedOptions,
                optionKey,
                options_cost: optionsCost
              }
            ]
          });
        }
      },

      removeItem: (productId, optionKey = '') => {
        set({
          items: get().items.filter(
            (i) => !(i.product_id === productId && (i.optionKey || '') === optionKey)
          )
        });
      },

      updateQuantity: (productId, quantity, optionKey = '') => {
        if (quantity <= 0) {
          get().removeItem(productId, optionKey);
        } else {
          set({
            items: get().items.map((i) =>
              i.product_id === productId && (i.optionKey || '') === optionKey
                ? { ...i, quantity }
                : i
            )
          });
        }
      },

      getTotal: () => {
        return get().items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      },

      getItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      clearCart: () => set({ items: [] })
    }),
    {
      name: 'glassorder-cart'
    }
  )
);

// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      activePortal: null,
      sessions: {},
      hydrated: false,

      setAuth: (token, user, portal = null) => {
        set({ token, user, activePortal: portal,
          sessions: { ...get().sessions, [portal]: { token, user } } });
      },

      activatePortal: (portal) => {
        const session = get().sessions[portal];
        set({ activePortal: portal, token: session?.token || null, user: session?.user || null });
      },

      logout: (portal = get().activePortal) => {
        const sessions = { ...get().sessions };
        delete sessions[portal];
        set({ sessions, ...(portal === get().activePortal ? { token: null, user: null } : {}) });
      },

      isAuthenticated: () => !!get().token,
      isSuperAdmin: () => get().user?.role === 'superadmin',
      isAdmin: () => ['admin', 'superadmin'].includes(get().user?.role),
      isStaff: () => ['staff', 'admin', 'superadmin'].includes(get().user?.role)
    }),
    {
      name: AUTH_STORAGE_KEY,
      version: 1,
      migrate: state => ({ sessions: state.sessions || {} }),
      storage: createJSONStorage(() => ({
        getItem: key => localStorage.getItem(key),
        setItem: (key, value) => { if (localStorage.getItem(key) !== value) localStorage.setItem(key, value); },
        removeItem: key => localStorage.removeItem(key)
      })),
      partialize: (state) => ({
        sessions: state.sessions
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hydrated = true;
        }
      }
    }
  )
);

// Order Tracking Store
export const useOrderStore = create(
  persist(
    (set) => ({
      activeOrderId: null,
      activeOrderStatus: null,
      activeTableNumber: null,

      setActiveOrder: (orderId, status, tableNumber) =>
        set({
          activeOrderId: orderId,
          activeOrderStatus: status,
          activeTableNumber: tableNumber
        }),

      updateOrderStatus: (status) => set({ activeOrderStatus: status }),

      clearActiveOrder: () =>
        set({
          activeOrderId: null,
          activeOrderStatus: null,
          activeTableNumber: null
        })
    }),
    {
      name: 'glassorder-order-tracking'
    }
  )
);

// UI Store
export const useUIStore = create(
  persist(
    (set) => ({
      soundEnabled: true,
      sidebarOpen: true,
      notifPrefs: {
        pending: true,
        preparing: true,
        ready: true
      },

      toggleSound: () => set((state) => ({ soundEnabled: !state.soundEnabled })),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setNotifPref: (status, enabled) =>
        set((state) => ({
          notifPrefs: { ...state.notifPrefs, [status]: enabled }
        }))
    }),
    {
      name: 'glassorder-ui'
    }
  )
);
